import { signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../environments/environment';

export class SignalRService {
    private hubConnection?: signalR.HubConnection;
    private currentHubUrl?: string;

    readonly isConnected = signal<boolean>(false);
    readonly connectionError = signal<string | null>(null);


    startConnection(hubUrl: string): Promise<void> {
        // Si déjà connecté au même hub, retourner la connexion existante
        if (this.hubConnection && this.currentHubUrl === hubUrl) {
            if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
                return Promise.resolve();
            }
            if (this.hubConnection.state === signalR.HubConnectionState.Connecting) {
                // Attendre que la connexion en cours se termine
                return new Promise((resolve, reject) => {
                    const checkConnection = () => {
                        if (this.hubConnection?.state === signalR.HubConnectionState.Connected) {
                            resolve();
                        } else if (this.hubConnection?.state === signalR.HubConnectionState.Disconnected) {
                            reject('Connection failed');
                        } else {
                            setTimeout(checkConnection, 100);
                        }
                    };
                    checkConnection();
                });
            }
        }

        // Fermer l'ancienne connexion si elle existe et qu'on change de hub
        if (this.hubConnection && this.currentHubUrl !== hubUrl) {
            this.hubConnection.stop();
        }

        const url = `${environment.signalRUrl}${hubUrl}`;
        this.currentHubUrl = hubUrl;

        this.hubConnection = new signalR.HubConnectionBuilder()
            .withUrl(url, {
                withCredentials: true
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    // Stratégie de reconnexion exponentielle avec un maximum de 60 secondes
                    const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 60000);
                    console.log(`SignalR: Next retry in ${delay}ms (attempt ${retryContext.previousRetryCount + 1})`);
                    return delay;
                }
            })
            .configureLogging(signalR.LogLevel.Information)
            .build();

        // Événements de connexion
        this.hubConnection.onreconnecting((error) => {
            this.isConnected.set(false);
            console.log('SignalR: Reconnecting...', error);
        });

        this.hubConnection.onreconnected((connectionId) => {
            this.isConnected.set(true);
            console.log('SignalR: Reconnected with connectionId:', connectionId);
        });

        this.hubConnection.onclose((error) => {
            this.isConnected.set(false);
            if (error) {
                this.connectionError.set(error.message);
                console.error('SignalR: Connection closed with error:', error);

                // Tenter de se reconnecter après une erreur d'authentification
                if (error.message.includes('401') || error.message.includes('Unauthorized')) {
                    console.log('SignalR: Authentication error detected, attempting to reconnect...');
                    setTimeout(() => this.startConnection(hubUrl), 5000);
                }
            } else {
                console.log('SignalR: Connection closed');
            }
        });

        // Démarrer la connexion
        return this.hubConnection
            .start()
            .then(() => {
                this.isConnected.set(true);
                this.connectionError.set(null);
                console.log('SignalR: Connected successfully');
            })
            .catch((error) => {
                this.connectionError.set(error.message);
            });
    }

    stopConnection(): Promise<void> {
        if (this.hubConnection) {
            return this.hubConnection.stop().then(() => {
                this.isConnected.set(false);
                this.currentHubUrl = undefined;
                console.log('SignalR: Connection stopped');
            });
        }
        return Promise.resolve();
    }

    // Obtenir l'état de la connexion
    getConnectionState(): signalR.HubConnectionState | undefined {
        return this.hubConnection?.state;
    }

    // Écouter un événement du serveur
    on<T = any>(eventName: string, callback: (data: T) => void): void {
        if (this.hubConnection) {
            this.hubConnection.on(eventName, callback);
        }
    }

    // Supprimer l'écoute d'un événement
    off(eventName: string): void {
        if (this.hubConnection) {
            this.hubConnection.off(eventName);
        }
    }

    // Invoquer une méthode sur le serveur
    invoke<T = any>(methodName: string, ...args: any[]): Promise<T> {
        if (!this.hubConnection) {
            return Promise.reject('Connection not established');
        }
        return this.hubConnection.invoke<T>(methodName, ...args);
    }

    // Envoyer un message sans attendre de réponse
    send(methodName: string, ...args: any[]): Promise<void> {
        if (!this.hubConnection) {
            return Promise.reject('Connection not established');
        }
        return this.hubConnection.send(methodName, ...args);
    }
}
